import React, { useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../../styles/admin/Navbar.css';
import '../../styles/Messaging.css';
import { FaHouseChimney } from "react-icons/fa6";
import { FaMoneyBillTrendUp } from "react-icons/fa6";
import { TbCalendarClock } from "react-icons/tb";
import { TbMessageDots } from "react-icons/tb";
import { IoMdLogOut } from "react-icons/io";
import { Users } from '../../models/User.tsx';
import { IoIosSearch } from "react-icons/io";
import { useUnreadMessages } from '../../hooks/useUnreadMessages.ts';
import { logUserActivity } from '../../services/player/LoginService.tsx';
import { useUser } from '../../context/UserContext.tsx';
import { applyLoginChrome } from '../../helpers/loginChrome.ts';
import AgentContext from '../../context/admin/Agent.tsx';
import { copyAgentLimitsToUsers, generateWeeklyFromSeptember, syncUsersPending } from '../../services/player/apis/Schelude.tsx';

function Navbar() {

    const { user: currentUser, userIpData: userIpDataAdmin, logout, } = useUser();
    const { allAgents, allPlayers } = useContext(AgentContext)
    const navigate = useNavigate();
    const [searchResults, setSearchResults] = useState<Users[]>([]);
    const [queryText, setQueryText] = useState('');
    const location = useLocation();
    const [showInput, setShowInput] = useState(false);
    const { unreadCount, showNewMessageToast, dismissToast } = useUnreadMessages(currentUser?.userName);
    const isLocalhost =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';
    const [generatingWeekly, setGeneratingWeekly] = useState(false);
    const [syncingPending, setSyncingPending] = useState(false);
    const [copyingAgentLimits, setCopyingAgentLimits] = useState(false);

    const handleGenerateWeeklyFromSeptember = async () => {
        if (generatingWeekly) return;
        setGeneratingWeekly(true);
        try {
            await generateWeeklyFromSeptember();
        } catch (error) {
            console.error(error);
        } finally {
            setGeneratingWeekly(false);
        }
    };


    const handleCopyAgentLimits = async () => {
        if (copyingAgentLimits) return;
        const confirmed = window.confirm(
            "Copy agent config onto matching agent users? This updates agentMax* and allow* fields.",
        );
        if (!confirmed) return;
        setCopyingAgentLimits(true);
        try {
            const result = await copyAgentLimitsToUsers();
            const missing = result.missing?.length
                ? result.missing.join(", ")
                : "none";
            window.alert(
                `Agents in config: ${result.agentsInConfig}\nUpdated: ${result.updated?.length ?? 0}\nMissing: ${missing}`,
            );
        } catch (error) {
            console.error(error);
            window.alert("Could not copy agent limits.");
        } finally {
            setCopyingAgentLimits(false);
        }
    };

    const handleSyncUsersPending = async () => {
        if (syncingPending) return;
        setSyncingPending(true);
        try {
            const result = await syncUsersPending();
            window.alert(
                `Pending synced.\nPlayers updated: ${result.playersUpdated}\nWith pending: ${result.playersWithPending}\nOpen bets: ${result.openBetsCounted}\nTotal pending: ${result.totalPending}`,
            );
        } catch (error) {
            console.error(error);
            window.alert("Could not sync pending.");
        } finally {
            setSyncingPending(false);
        }
    };

    const handleSearchIconClick = () => {
        setShowInput(!showInput);
    };

    const filterUsers = (users: Users[], queryText: string) => {
        const text = queryText.toLowerCase().trim();

        return users.filter(user => {
            const userName = (user.userName || "").toLowerCase();
            const name = (user.name || "").toLowerCase();
            const pin = String(user.pin || "").toLowerCase();

            return (
                userName.includes(text) ||
                name.includes(text) ||
                pin.includes(text)
            );
        });
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setQueryText(value);

        if (value.trim() === '') {
            setSearchResults([]);
            return;
        }
        const results = filterUsers(
            [...allAgents, ...allPlayers],
            value
        );

        setSearchResults(results);
    };

    const handleResultSelect = async (selectedUser: Users) => {
        setSearchResults([]);
        setQueryText('');
        setShowInput(false);

        navigate(`/user/${selectedUser.userName}`, {
            state: {
                userId: selectedUser.id,
                visitedUserPreview: selectedUser,
            },
        });

        if (currentUser?.userName) {
            try {
                await logUserActivity(
                    currentUser.userName,
                    `View User Basic: ${selectedUser.userName}`,
                    userIpDataAdmin
                );
            } catch (error) {
                console.error(error);
            }
        }
    };

    const handleLogout = () => {
        applyLoginChrome();
        logout();
        navigate('/');
    };

    return currentUser == null ? 'Loading' : (
        <>
            <nav className="background spacing-navbar">
                <div className='navbarContainer'>
                    <div className='left-section'>
                        <Link to="/homeAdmin" className="no-link-admin" onClick={() => logUserActivity(currentUser!.userName, "View Home", userIpDataAdmin)}>
                            <div className='color-icon icon-home'>
                                <FaHouseChimney className='icon-navbar-admin' />
                            </div>
                        </Link>
                        <Link to="/weekly-figures" className="no-link-admin" onClick={() => logUserActivity(currentUser!.userName, "View Weekly Figures", userIpDataAdmin)}>
                            <div className='color-icon'>
                                <FaMoneyBillTrendUp className={`icon-navbar-admin ${location.pathname === '/weekly-figures' ? 'selected' : ''}`} />
                            </div>
                        </Link>
                        <Link to="/pendingBets" className="no-link-admin screens" onClick={() => logUserActivity(currentUser!.userName, "View Pending Bets", userIpDataAdmin)}>

                            <div className='color-icon'>
                                <TbCalendarClock className={`icon-navbar-admin ${location.pathname === '/pendingBets' ? 'selected' : ''}`} />
                            </div>
                        </Link>
                        <Link to="/messagingAdmin" className="no-link-admin screens" onClick={() => logUserActivity(currentUser!.userName, "View Messaging", userIpDataAdmin)}>
                            <div className='color-icon admin-messaging-icon-wrap'>
                                <TbMessageDots className={`icon-navbar-admin ${location.pathname === '/messagingAdmin' ? 'selected' : ''}`} />
                                {unreadCount > 0 && (
                                    <span className="admin-messaging-badge">
                                        {unreadCount > 99 ? '99+' : unreadCount}
                                    </span>
                                )}
                            </div>
                        </Link>
                        <div className='search-container-navbar'>
                            <IoIosSearch className="search-icon" onClick={handleSearchIconClick} />
                            <input
                                id="recipientUsers"
                                name="usersNav"
                                type="text"
                                className={`input-user ${showInput ? 'show' : ''}`}
                                placeholder="Search accounts"
                                value={queryText}
                                onChange={handleInputChange}
                                autoComplete="off"
                            />
                            {searchResults.length > 0 && (
                                <ul className="list-user overflow-auto" style={{ maxHeight: "300px" }}>
                                    {searchResults.map(user => (
                                        <li key={user.id} className='list-user-item' onClick={() => handleResultSelect(user)}>
                                            <div className='d-flex justify-content-between'>
                                                <div>
                                                    <span className='search-username-navbar'>{user.userName}</span>
                                                    <span className='search-pin-name-navbar'>({user.pin})</span>
                                                </div>

                                                <div>
                                                    <span className='search-pin-name-navbar'>{user.name}</span>
                                                </div>
                                            </div>

                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <div className='right-section'>
                        {/* {isLocalhost && (
                            <button
                                type="button"
                                disabled={generatingWeekly}
                                onClick={handleGenerateWeeklyFromSeptember}
                            >
                                {generatingWeekly ? 'Generating…' : 'Weekly from Sep'}
                            </button>
                        )} */}
                        {/* <button
                            type="button"
                            className="admin-nav-action"
                            disabled={copyingAgentLimits}
                            onClick={handleCopyAgentLimits}
                        >
                            {copyingAgentLimits ? 'Copying…' : 'Copy agent config'}
                        </button> */}
                        {/* <button
                            type="button"
                            className="admin-nav-action"
                            disabled={syncingPending}
                            onClick={handleSyncUsersPending}
                        >
                            {syncingPending ? 'Syncing…' : 'Sync pending'}
                        </button> */}
                        <div id="userNameDisplay" className='user-name teko'>{currentUser!.userName}</div>
                        <button id="logoutButton" className='logout' onClick={handleLogout}>
                            <IoMdLogOut className='icon-navbar-admin' />
                        </button>
                    </div>
                </div>
            </nav>

            {showNewMessageToast && (
                <div className="message-toast-bubble" onClick={dismissToast}>
                    <TbMessageDots className="message-toast-icon" />
                    <div className="message-toast-content">
                        <span className="message-toast-title">New message</span>
                        <span className="message-toast-text">You have {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}</span>
                    </div>
                    <Link to="/messagingAdmin" className="message-toast-link" onClick={() => { dismissToast(); logUserActivity(currentUser!.userName, "View Messaging", userIpDataAdmin); }}>
                        View
                    </Link>
                </div>
            )}
        </>
    );
};

export default Navbar;